import { makeTestId } from "app/client/lib/domUtils";
import { makeT } from "app/client/lib/localization";
import { getTimeFromNow } from "app/client/lib/timeUtils";
import { transientInput } from "app/client/ui/transientInput";
import { theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import {
  gristFloatingMenuClass,
  menu,
  menuCssClass,
  menuItem,
  searchableMenu,
  SearchableMenuItem,
} from "app/client/ui2018/menus";
import { AssistantSessionStore, AssistantSessionSummary } from "app/client/widgets/AssistantSessionStore";

import { Computed, dom, DomElementArg, Observable, styled, UseCB } from "grainjs";
import sortBy from "lodash/sortBy";
import { cssMenuItem as weaselMenuItem } from "popweasel";

// eslint-disable-next-line local/makeT-filename
const t = makeT("Assistant");

const testId = makeTestId("test-assistant-");

export interface AssistantHistoryMenuOptions {
  /** Called when the user deletes session `id`. */
  onDelete(id: string): void;
}

/**
 * Builds a menu for searching, switching, renaming, and deleting the sessions in `store`.
 */
export function assistantHistoryMenu(store: AssistantSessionStore, options: AssistantHistoryMenuOptions) {
  return menu(() => buildHistoryMenu(store, options), {
    menuCssClass: `${menuCssClass} ${gristFloatingMenuClass} ${cssHistoryMenu.className}`,
    allowNothingSelected: true,
  });
}

function buildHistoryMenu(store: AssistantSessionStore, options: AssistantHistoryMenuOptions): DomElementArg[] {
  const renamingId = Observable.create<string | null>(null, null);

  const order = new Map(store.sessions.get().map((session, i) => [session.id, i]));
  const position = (session: AssistantSessionSummary) => order.get(session.id) ?? -1;

  // While renaming, keep the same items so that saving the new title doesn't rebuild the row being edited.
  const items: Computed<SearchableMenuItem[]> = Computed.create(null, use =>
    use(renamingId) ? items.get() : sortBy(use(store.sessions), position).map(session => ({
      cleanText: session.title.toLowerCase(),
      builder: () => buildHistoryRow(store, session, renamingId, options),
    })));

  return [
    dom.autoDispose(renamingId),
    dom.autoDispose(items),
    ...searchableMenu(items, {
      searchInputPlaceholder: t("Search sessions..."),
      searchInputArgs: [
        // The menu focuses itself after a timeout when opened, so wait until after that.
        (elem: HTMLElement) => { setTimeout(() => elem.focus(), 10); },
        testId("history-search"),
      ],
      buildEmptyMessage: () => cssHistoryEmpty(t("No sessions found"), testId("history-empty")),
    }),
    dom.maybe(use => !use(store.isPersistent), () =>
      cssHistoryUnavailable(
        t("Sessions won't be saved after you leave this page."),
        testId("history-unavailable"),
      ),
    ),
  ];
}

function buildHistoryRow(
  store: AssistantSessionStore,
  session: AssistantSessionSummary,
  renamingId: Observable<string | null>,
  { onDelete }: AssistantHistoryMenuOptions,
) {
  const isRenaming = (use: UseCB) => use(renamingId) === session.id;
  const isCurrent = (use: UseCB) => use(store.activeSessionId) === session.id;
  const closeEditor = () => {
    if (!renamingId.isDisposed()) { renamingId.set(null); }
  };
  return menuItem(
    () => store.switchSession(session.id),
    dom.cls(cssHistoryItem.className),
    dom.cls(cssHistoryItemCurrent.className, isCurrent),
    dom.cls("test-assistant-history-item-current", isCurrent),
    dom.domComputed(isRenaming, renaming => renaming ?
      cssHistoryEditor(
        {
          initialValue: session.title,
          save: async value => store.renameSession(session.id, value),
          close: closeEditor,
        },
        // Don't let clicks in the editor trigger the menu item.
        dom.on("mousedown", ev => ev.stopPropagation()),
        dom.on("click", (ev) => { ev.stopPropagation(); ev.preventDefault(); }),
        testId("history-item-editor"),
      ) :
      [
        cssHistoryItemTitle(session.title, testId("history-item-title")),
        cssHistoryItemSlot(
          cssHistoryItemTime(
            getTimeFromNow(session.updatedAt),
            testId("history-item-time"),
          ),
          cssHistoryItemButtons(
            cssHistoryItemButton(
              icon("Pencil"),
              dom.on("click", (ev) => {
                ev.stopPropagation();
                renamingId.set(session.id);
              }),
              testId("history-item-rename"),
            ),
            cssHistoryItemButton(
              icon("Remove"),
              dom.on("click", (ev) => {
                ev.stopPropagation();
                onDelete(session.id);
              }),
              testId("history-item-delete"),
            ),
          ),
        ),
      ],
    ),
    testId("history-item"),
  );
}

const cssHistoryMenu = styled("div", `
  && {
    width: 280px;
    max-height: 420px;
    overflow-y: auto;
    padding: 6px;
    border: 1px solid ${theme.menuBorder};
    border-radius: 6px;
  }
`);

const cssHistoryEmpty = styled("div", `
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 20px;
  padding: 6px 10px;
  color: ${theme.lightText};
`);

const cssHistoryUnavailable = styled("div", `
  line-height: 16px;
  padding: 8px 10px 6px 10px;
  text-align: center;
  color: ${theme.lightText};
  font-size: ${vars.smallFontSize};
`);

const cssHoverBg = `color-mix(in srgb, ${theme.hover} 48%, transparent)`;
const cssCurrentBg = `color-mix(in srgb, ${theme.hover} 75%, transparent)`;
const cssIconHoverBg = `color-mix(in srgb, ${theme.hover} 80%, transparent)`;

const cssHistoryItem = styled("div", `
  && {
    display: flex;
    align-items: center;
    column-gap: 8px;
    padding: 6px 10px;
    border-radius: 4px;
  }
  &&:hover, &&.${weaselMenuItem.className}-sel {
    background-color: ${cssHoverBg};
    color: ${theme.menuItemFg};
    --icon-color: ${theme.lightText};
  }
  && + && {
    margin-top: 4px;
  }
`);

const cssHistoryItemCurrent = styled("div", `
  &&, &&:hover, &&.${weaselMenuItem.className}-sel {
    background-color: ${cssCurrentBg};
    font-weight: 600;
  }
`);

const cssHistoryItemTitle = styled("div", `
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`);

const cssHistoryItemSlot = styled("div", `
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 44px;
  height: 20px;
`);

const cssHistoryItemTime = styled("div", `
  color: ${theme.lightText};
  font-size: ${vars.smallFontSize};

  .${cssHistoryItem.className}:hover &, .${cssHistoryItem.className}.${weaselMenuItem.className}-sel & {
    display: none;
  }
  @media (hover: none) {
    & {
      display: none;
    }
  }
`);

const cssHistoryItemButtons = styled("div", `
  display: none;
  align-items: center;
  column-gap: 4px;

  .${cssHistoryItem.className}:hover &, .${cssHistoryItem.className}.${weaselMenuItem.className}-sel & {
    display: flex;
  }
  @media (hover: none) {
    & {
      display: flex;
    }
  }
`);

const cssHistoryItemButton = styled("div", `
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  --icon-color: ${theme.lightText};

  &:hover {
    background-color: ${cssIconHoverBg};
  }
`);

const cssHistoryEditor = styled(transientInput, `
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  height: 20px;
  padding: 0px;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: inherit;
  color: ${theme.inputFg};
  background-color: transparent;
`);
